import { EduSystem } from '@lessonup/teaching-core';
import { compactMap } from '@lessonup/utils';
import { includes } from 'lodash';
import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  FacetKey,
  FacetResult,
  Facets,
  isFacetVisibleForUser,
  LanguageSingleton,
  SelectedFacet,
} from '../../../../domain';
import {
  createLabelFormatter,
  createOption,
  formatFacetPlaceholder,
  getOptions,
  translatedFacetPlaceholder,
} from '../../../../utils/FacetUtils';
import FacetSelect, { Option } from './FacetSelect';

const CREATABLE_FACETS: FacetKey[] = ['subjects'];

interface FacetSelectWithOptionsProps {
  facetResult: FacetResult;
  selectedSchoolType: string | undefined;
  selectedFacets: SelectedFacet[];
  eduSystem: EduSystem.System;
  country: string;
  handleFacetChange: (value: Option) => void;
  hideCounts?: boolean;
  isMulti?: boolean;
}

const FacetSelectWithOptions: React.FC<FacetSelectWithOptionsProps> = ({
  facetResult,
  selectedSchoolType,
  selectedFacets,
  eduSystem,
  country,
  handleFacetChange,
  hideCounts,
  isMulti = false,
}) => {
  const { t } = useTranslation(['facets', 'slides', 'sources']);

  const labelFormatter = createLabelFormatter(
    facetResult.key,
    selectedSchoolType,
    eduSystem,
    country,
    LanguageSingleton.get()
  );

  const options = getOptions(
    facetResult.key,
    selectedSchoolType,
    eduSystem,
    compactMap(facetResult.items, (item) => createOption(item, labelFormatter)),
    t,
    hideCounts
  );
  if (!isFacetVisibleForUser(facetResult.key)) return null;

  const selectedFacet = selectedFacets.find((f) => f.key === facetResult.key);

  const selectedOptions =
    selectedFacet?.value.map((value) => {
      const option = options.find((option) => option.value === value);
      if (option) return option;

      // We have a fallback for when users select an unknown option.
      // This can occur if there are no more lessons with the selected option, or if a deeplink was modified.
      // Adding an option allows the user to clear the selection.
      return {
        value,
        label: translatedFacetPlaceholder(selectedFacet.key, value, t),
        count: undefined,
      };
    }) ?? [];

  const isDisabled =
    facetResult.isDisabled ||
    Facets.facetsHasUnmetDependency(facetResult.key, selectedFacets) ||
    (!selectedFacet && options.length === 0);

  const placeholder = formatFacetPlaceholder(facetResult.key, eduSystem, selectedSchoolType, t);

  const isMultiEnabled = isMulti && (facetResult.key === 'years' || selectedOptions.length > 1);

  return (
    <FacetSelect
      key={facetResult.key}
      facetKey={facetResult.key}
      placeholder={placeholder}
      isMulti={isMultiEnabled}
      options={options}
      onChange={handleFacetChange}
      isDisabled={isDisabled}
      selectedOptions={selectedOptions}
      isCreatable={includes(CREATABLE_FACETS, facetResult.key)}
    ></FacetSelect>
  );
};

export default FacetSelectWithOptions;
